import { formatDateTime } from '@/utils/datetime';
import { dash, printReport } from '@/pages/Batch-Processing/reports/reportStyle';

const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const fmtDt = (v) => (v ? formatDateTime(v, 'DD-MMM-YYYY HH:mm') : '—');

const statusClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('complete') || s.includes('approv')) return 'completed';
  if (s.includes('progress') || s.includes('running') || s.includes('start')) return 'in-progress';
  if (s.includes('reject') || s.includes('fail')) return 'rejected';
  return 'pending';
};

const statusPill = (status) =>
  `<span class="status ${statusClass(status)}">${esc(status || '—')}</span>`;

const sectionTitle = (title) =>
  `<div class="section-title"><table><tbody><tr><th class="header-class">${esc(title)}</th></tr></tbody></table></div>`;

const cleaningTypeName = (type) => {
  if (!type) return '—';
  if (typeof type === 'string') return type;
  return type.name || '—';
};

const cleaningTypeCategory = (type) => {
  if (!type || typeof type === 'string') return '';
  return type.category || '';
};

/**
 * Build A4 Cleaning Logs HTML from loaded logData and open print → Save as PDF.
 * Supports both batch-filter and equipment-filter payloads.
 */
export function exportCleaningLogsPdf(logData, { mbmrAlias = 'MBMR' } = {}) {
  if (!logData) return false;

  const activities = logData.cleaning_activities || [];
  const isBatch = !!logData.batch_number;
  const titleRef = isBatch
    ? logData.batch_number
    : logData.equipment_name || logData.equipment_id || 'report';

  let headerHtml = '';
  if (isBatch) {
    headerHtml = `
      ${sectionTitle('Batch Details')}
      <table>
        <tbody>
          <tr>
            <td><b>Batch Number:</b> ${esc(dash(logData.batch_number))}</td>
            <td><b>${esc(mbmrAlias)} Name:</b> ${esc(dash(logData.recipe_name))}</td>
            <td><b>Product:</b> ${esc(dash(logData.product_code))}</td>
          </tr>
        </tbody>
      </table>`;
  } else {
    headerHtml = `
      ${sectionTitle('Equipment Details')}
      <table>
        <tbody>
          <tr>
            <td><b>Equipment ID:</b> ${esc(dash(logData.equipment_id))}</td>
            <td><b>Equipment Name:</b> ${esc(dash(logData.equipment_name))}</td>
            <td><b>Type:</b> ${esc(dash(logData.equipment_type || logData.equipment_code))}</td>
          </tr>
        </tbody>
      </table>`;
  }

  let activitiesHtml = sectionTitle('Cleaning Activities');
  activitiesHtml += `<table>
    <thead>
      <tr class="header-class-1">
        <th style="width:16%">Equipment</th>
        <th style="width:10%">Type</th>
        <th style="width:10%">Area</th>
        <th style="width:14%">Cleaning Type</th>
        <th style="width:10%">Status</th>
        <th style="width:12%">Start</th>
        <th style="width:12%">End</th>
        <th style="width:8%">Duration</th>
        <th style="width:8%">Started By</th>
      </tr>
    </thead>
    <tbody>
      ${
        activities.length
          ? activities
              .map((a) => {
                const cat = cleaningTypeCategory(a.cleaning_type);
                return `<tr>
                  <td>
                    ${esc(a.equipment_name || '—')}<br/>
                    <span style="font-size:10px;color:#666">${esc(a.equipment_code || a.equipment_id || '')}</span>
                  </td>
                  <td class="table_data">${esc(a.equipment_type || '—')}</td>
                  <td class="table_data">${esc(a.area || '—')}</td>
                  <td class="table_data">
                    ${esc(cleaningTypeName(a.cleaning_type))}
                    ${cat ? `<br/><span style="font-size:10px;color:#666">${esc(cat)}</span>` : ''}
                  </td>
                  <td class="table_data">${statusPill(a.status)}</td>
                  <td class="table_data">${esc(fmtDt(a.start_time))}</td>
                  <td class="table_data">${esc(fmtDt(a.end_time))}</td>
                  <td class="table_data">${esc(a.duration_minutes != null ? `${a.duration_minutes} min` : '—')}</td>
                  <td class="table_data">${esc(a.started_by || '—')}</td>
                </tr>`;
              })
              .join('')
          : `<tr><td colspan="9" class="table_data">No cleaning activities found</td></tr>`
      }
    </tbody>
  </table>`;

  let detailHtml = '';
  activities.forEach((activity, idx) => {
    const steps = activity.cleaning_steps || [];
    if (!steps.length && !activity.completed_by && !activity.cleaning_procedure) return;

    detailHtml += `<div class="card" style="margin-top:12px;">
      <h2>Activity ${idx + 1} — ${esc(activity.equipment_name || 'Equipment')} (${esc(activity.equipment_code || '')})</h2>
      <table>
        <tbody>
          <tr>
            <td><b>Status:</b> ${statusPill(activity.status)}</td>
            <td><b>Completed By:</b> ${esc(dash(activity.completed_by))}</td>
            <td><b>Duration:</b> ${esc(activity.duration_minutes != null ? `${activity.duration_minutes} min` : '—')}</td>
          </tr>
          ${
            activity.cleaning_procedure
              ? `<tr><td colspan="3"><b>Procedure:</b> ${esc(activity.cleaning_procedure)}</td></tr>`
              : ''
          }
          ${
            activity.cleaning_agents_used
              ? `<tr><td colspan="3"><b>Agents Used:</b> ${esc(activity.cleaning_agents_used)}</td></tr>`
              : ''
          }
          ${
            activity.verification_method || activity.verification_results
              ? `<tr>
                  <td colspan="2"><b>Verification:</b> ${esc(dash(activity.verification_method))}</td>
                  <td><b>Results:</b> ${esc(dash(activity.verification_results))}</td>
                </tr>`
              : ''
          }
        </tbody>
      </table>`;

    if (steps.length) {
      detailHtml += `<h3 style="font-size:13px;color:#1a73e8;margin:12px 0 4px;">Cleaning Steps</h3>
        <table>
          <thead>
            <tr class="header-class-1">
              <th style="width:6%">#</th>
              <th style="width:18%">Step</th>
              <th style="width:12%">Status</th>
              <th style="width:14%">Performed By</th>
              <th style="width:14%">Start</th>
              <th style="width:14%">End</th>
              <th style="width:22%">Remarks</th>
            </tr>
          </thead>
          <tbody>
            ${steps
              .map(
                (s) =>
                  `<tr>
                    <td class="table_data">${esc(s.step_number ?? '')}</td>
                    <td>${esc(s.step_name || '—')}</td>
                    <td class="table_data">${statusPill(s.status)}</td>
                    <td class="table_data">${esc(s.performed_by || '—')}</td>
                    <td class="table_data">${esc(fmtDt(s.start_time))}</td>
                    <td class="table_data">${esc(fmtDt(s.end_time))}</td>
                    <td>${esc(s.remarks || s.description || '—')}</td>
                  </tr>`
              )
              .join('')}
          </tbody>
        </table>`;
    }

    detailHtml += `</div>`;
  });

  const html = `
    <div class="bmr-report">
      <div class="container">
        <header>
          <h1>Cleaning Logs Report</h1>
          <p>${esc(titleRef)}</p>
        </header>
        ${headerHtml}
        ${activitiesHtml}
        ${detailHtml}
        <p style="margin-top:24px;font-size:10px;color:#777;text-align:right;">
          Generated on ${esc(formatDateTime(new Date().toISOString(), 'DD-MMM-YYYY HH:mm'))}
        </p>
      </div>
    </div>
  `;

  const container = document.createElement('div');
  container.innerHTML = html;
  const root = container.firstElementChild;
  if (!root) return false;

  printReport(root, `Cleaning_Logs_${String(titleRef).replace(/[^\w.-]+/g, '_')}`);
  return true;
}
